import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
    Box, Typography, Button, Paper, Table, TableHead, TableBody,
    TableRow, TableCell, Chip, IconButton, Dialog, DialogTitle,
    DialogContent, DialogActions, TextField, CircularProgress, Alert,
    FormControl, InputLabel, Select, OutlinedInput, MenuItem,
    Checkbox, ListItemText, InputAdornment, Autocomplete,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { toast } from 'react-toastify';
import { directorAPI, publicAPI } from '../../services/api';
import ConfirmDialog from '../../components/common/ConfirmDialog';
import useAuth from '../../hooks/useAuth';

const PER_PAGE = 50;

export default function DirectorFilieres() {
    const { user } = useAuth();
    const isAdmin = user?.role === 'admin';

    const [filieres, setFilieres] = useState([]);
    const [loading, setLoading] = useState(true);
    const [initialLoading, setInitialLoading] = useState(true);
    const [loadingMore, setLoadingMore] = useState(false);
    const [page, setPage] = useState(1);
    const [lastPage, setLastPage] = useState(1);
    const [dialog, setDialog] = useState(false);
    const [istas, setIstas] = useState([]);
    const [form, setForm] = useState({ nom: '', code: '', description: '', is_active: true, module_ids: [], ista_ids: [] });
    const [editing, setEditing] = useState(null);
    const [deleteId, setDeleteId] = useState(null);
    const [error, setError] = useState('');
    const [searchTerm, setSearchTerm] = useState('');
    const [debouncedSearch, setDebouncedSearch] = useState('');

    // Module picker (async search Autocomplete) — never loads all modules at once
    const [moduleOptions, setModuleOptions] = useState([]);
    const [moduleSelections, setModuleSelections] = useState([]); // full module objects
    const [moduleSearch, setModuleSearch] = useState('');
    const [moduleLoading, setModuleLoading] = useState(false);

    const scrollRef = useRef(null);

    // Load istas once
    useEffect(() => {
        publicAPI.getIstas().then(res => setIstas(res.data)).catch(console.error);
    }, []);

    // Debounce table search (server-side, across ALL filieres)
    useEffect(() => {
        const t = setTimeout(() => setDebouncedSearch(searchTerm.trim()), 350);
        return () => clearTimeout(t);
    }, [searchTerm]);

    const fetchFilieres = useCallback(async (pageToLoad, search) => {
        if (pageToLoad === 1) setLoading(true); else setLoadingMore(true);
        try {
            const res = await directorAPI.getFilieres({ per_page: PER_PAGE, page: pageToLoad, search: search || undefined });
            const { data, current_page, last_page } = res.data;
            setFilieres(prev => pageToLoad === 1 ? data : [...prev, ...data]);
            setPage(current_page);
            setLastPage(last_page);
        } catch (err) { console.error(err); }
        finally { setLoading(false); setLoadingMore(false); setInitialLoading(false); }
    }, []);

    useEffect(() => {
        if (scrollRef.current) scrollRef.current.scrollTop = 0;
        fetchFilieres(1, debouncedSearch);
    }, [debouncedSearch, fetchFilieres]);

    const reload = () => fetchFilieres(1, debouncedSearch);

    const handleScroll = (e) => {
        const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
        if (scrollHeight - scrollTop - clientHeight < 300 && !loadingMore && page < lastPage) {
            fetchFilieres(page + 1, debouncedSearch);
        }
    };

    // ── Async module search for the picker ───────────────────────
    useEffect(() => {
        if (!dialog) return;
        const t = setTimeout(async () => {
            setModuleLoading(true);
            try {
                const res = await directorAPI.getModules({ per_page: 30, search: moduleSearch || undefined });
                setModuleOptions(res.data.data || []);
            } catch (err) { console.error(err); }
            finally { setModuleLoading(false); }
        }, 300);
        return () => clearTimeout(t);
    }, [moduleSearch, dialog]);

    const handleOpen = (fil = null) => {
        if (fil) {
            setEditing(fil.id);
            const mods = fil.modules || [];
            setModuleSelections(mods);
            setForm({
                nom: fil.nom,
                code: fil.code,
                description: fil.description || '',
                is_active: fil.is_active,
                module_ids: mods.map(m => m.id),
                ista_ids: fil.istas ? fil.istas.map(i => i.id) : []
            });
        } else {
            setEditing(null);
            setModuleSelections([]);
            setForm({ nom: '', code: '', description: '', is_active: true, module_ids: [], ista_ids: [] });
        }
        setModuleSearch('');
        setModuleOptions([]);
        setError('');
        setDialog(true);
    };

    const handleSave = async () => {
        setError('');
        try {
            if (editing) {
                await directorAPI.updateFiliere(editing, form);
                toast.success('Filière modifiée');
            } else {
                await directorAPI.createFiliere(form);
                toast.success('Filière créée');
            }
            setDialog(false);
            reload();
        } catch (err) {
            setError(err.response?.data?.message || 'Erreur');
        }
    };

    const handleDelete = async () => {
        try {
            await directorAPI.deleteFiliere(deleteId);
            toast.success('Filière supprimée');
            setDeleteId(null);
            reload();
        } catch (err) {
            toast.error('Erreur lors de la suppression');
        }
    };

    if (initialLoading) return <Box sx={{ display: 'flex', justifyContent: 'center', py: 10 }}><CircularProgress /></Box>;

    return (
        <Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3, alignItems: 'center', gap: 2 }}>
                <Typography variant="h5" fontWeight={700}>Gérer les filières</Typography>

                <Box sx={{ display: 'flex', gap: 2, flex: 1, justifyContent: 'flex-end' }}>
                    <TextField
                        size="small"
                        placeholder="Rechercher une filière..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        sx={{ width: 300, bgcolor: 'white', borderRadius: 2 }}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchIcon sx={{ color: 'text.secondary' }} />
                                </InputAdornment>
                            ),
                        }}
                    />
                    <Button variant="contained" startIcon={<AddIcon />} onClick={() => handleOpen()}
                        sx={{ background: 'linear-gradient(135deg, #1a5276, #2e86c1)', textTransform: 'none', borderRadius: 2 }}>
                        Ajouter filière
                    </Button>
                </Box>
            </Box>

            <Paper sx={{ borderRadius: 3, overflow: 'hidden' }}>
                <Box ref={scrollRef} onScroll={handleScroll} sx={{ maxHeight: 'calc(100vh - 230px)', overflow: 'auto' }}>
                <Table stickyHeader>
                    <TableHead sx={{ bgcolor: '#f8f9fa' }}>
                        <TableRow>
                            <TableCell sx={{ fontWeight: 700 }}>Code</TableCell>
                            <TableCell sx={{ fontWeight: 700 }}>Nom</TableCell>
                            <TableCell sx={{ fontWeight: 700 }}>Description</TableCell>
                            {isAdmin && <TableCell sx={{ fontWeight: 700 }}>ISTAs Affectés</TableCell>}
                            <TableCell sx={{ fontWeight: 700 }}>Modules</TableCell>
                            <TableCell sx={{ fontWeight: 700 }}>Offres</TableCell>
                            <TableCell sx={{ fontWeight: 700 }} align="right">Actions</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {filieres.map(f => (
                            <TableRow key={f.id} hover>
                                <TableCell><Chip label={f.code} size="small" variant="outlined" /></TableCell>
                                <TableCell><Typography variant="body2" fontWeight={600}>{f.nom}</Typography></TableCell>
                                <TableCell><Typography variant="body2" color="text.secondary">{f.description || '—'}</Typography></TableCell>
                                {isAdmin && (
                                    <TableCell>
                                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                            {f.istas?.map(i => (
                                                <Chip key={i.id} label={i.code} size="small" color="primary" variant="outlined" />
                                            ))}
                                            {(!f.istas || f.istas.length === 0) && <Typography variant="caption" color="text.secondary">Aucun</Typography>}
                                        </Box>
                                    </TableCell>
                                )}
                                <TableCell><Chip label={f.modules_count || 0} size="small" color="primary" variant="outlined" /></TableCell>
                                <TableCell><Chip label={f.job_offers_count || 0} size="small" color="secondary" variant="outlined" /></TableCell>
                                <TableCell align="right">
                                    <IconButton onClick={() => handleOpen(f)} color="primary" size="small"><EditIcon fontSize="small" /></IconButton>
                                    <IconButton onClick={() => setDeleteId(f.id)} color="error" size="small"><DeleteIcon fontSize="small" /></IconButton>
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
                {(loadingMore || loading) && (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}><CircularProgress size={28} /></Box>
                )}
                {!loading && !loadingMore && filieres.length === 0 && (
                    <Box sx={{ textAlign: 'center', py: 4, color: 'text.secondary' }}>Aucune filière trouvée</Box>
                )}
                </Box>
            </Paper>

            <Dialog open={dialog} onClose={() => setDialog(false)} maxWidth="sm" fullWidth>
                <DialogTitle>{editing ? 'Modifier la filière' : 'Nouvelle filière'}</DialogTitle>
                <DialogContent>
                    {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
                    <TextField fullWidth label="Nom" value={form.nom} onChange={(e) => setForm({ ...form, nom: e.target.value })} sx={{ mt: 1, mb: 2 }} />
                    <TextField fullWidth label="Code" value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value })} sx={{ mb: 2 }} />
                    <TextField fullWidth multiline rows={2} label="Description" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} sx={{ mb: 2 }} />

                    <Autocomplete
                        multiple
                        filterSelectedOptions
                        options={moduleOptions}
                        value={moduleSelections}
                        loading={moduleLoading}
                        isOptionEqualToValue={(opt, val) => opt.id === val.id}
                        getOptionLabel={(m) => `${m.name} (${m.code})`}
                        onInputChange={(e, val) => setModuleSearch(val)}
                        onChange={(e, val) => {
                            setModuleSelections(val);
                            setForm(prev => ({ ...prev, module_ids: val.map(m => m.id) }));
                        }}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                label="Modules de cette filière"
                                placeholder="Rechercher un module..."
                                sx={{ mb: 2 }}
                                InputProps={{
                                    ...params.InputProps,
                                    endAdornment: (
                                        <>
                                            {moduleLoading ? <CircularProgress color="inherit" size={18} /> : null}
                                            {params.InputProps.endAdornment}
                                        </>
                                    ),
                                }}
                            />
                        )}
                    />

                    {isAdmin && (
                        <FormControl fullWidth margin="dense">
                            <InputLabel>Affecter à des ISTAs</InputLabel>
                            <Select
                                multiple
                                value={form.ista_ids}
                                onChange={(e) => setForm({ ...form, ista_ids: e.target.value })}
                                input={<OutlinedInput label="Affecter à des ISTAs" />}
                                renderValue={(selected) => (
                                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                        {selected.map((value) => (
                                            <Chip key={value} label={istas.find(i => i.id === value)?.code || value} size="small" />
                                        ))}
                                    </Box>
                                )}
                            >
                                {istas.map((i) => (
                                    <MenuItem key={i.id} value={i.id}>
                                        <Checkbox checked={form.ista_ids.indexOf(i.id) > -1} />
                                        <ListItemText primary={`${i.code} - ${i.nom}`} />
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDialog(false)}>Annuler</Button>
                    <Button onClick={handleSave} variant="contained" sx={{ bgcolor: '#1a5276' }}>Enregistrer</Button>
                </DialogActions>
            </Dialog>

            <ConfirmDialog
                open={!!deleteId}
                title="Supprimer la filière ?"
                message="Toutes les données liées seront conservées mais la filière sera supprimée."
                onConfirm={handleDelete}
                onCancel={() => setDeleteId(null)}
            />
        </Box>
    );
}
